import {
  Box,
  ChartContainer,
  ChartTooltip,
  Line,
  Skeleton,
  Stack,
  tokens,
  YAxis,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { formatPercentage } from '@cast/utils';

import { InventoryListItem } from '../../../_components/ui/InventoryListItem';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import {
  ResourceUtilizationPoint,
  useResourceUtilizationData,
} from './_hooks/useResourceUtilizationData';

const CPU_COLOR = tokens.theme.data.blue300;
const MEMORY_COLOR = tokens.theme.data.blue700;
const SPARKLINE_HEIGHT = 120;

const ColorMarker = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      minWidth: 8,
      minHeight: 8,
      borderRadius: tokens.theme.radius.xs,
      backgroundColor: color,
    }}
  />
);

const ResourceUtilizationCardSkeleton = () => (
  <Stack gap={16} height="100%">
    <Box flex={1} minHeight={SPARKLINE_HEIGHT}>
      <Skeleton variant="rectangular" height="100%" />
    </Box>
    <Stack>
      <Skeleton variant="text" height={24} sx={{ my: 6 }} />
      <Skeleton variant="text" height={24} sx={{ my: 6 }} />
    </Stack>
  </Stack>
);

export const ResourceUtilizationCard = () => {
  const { points, cpuPct, memoryPct, isLoading, isFetching, error, refetch } =
    useResourceUtilizationData();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  return (
    <WidgetLayout
      title="Resource utilization"
      testId="resource-utilization-card"
      headerDivider={false}
      slotProps={{ header: { py: 12 } }}
    >
      {isCardLoading ? (
        <ResourceUtilizationCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load resource utilization"
          refresh={refetch}
          compact
        />
      ) : (
        <Stack gap={16} height="100%">
          <Box flex={1} minHeight={SPARKLINE_HEIGHT} width="100%">
            <ChartContainer<ResourceUtilizationPoint>
              data={points}
              isLoading={false}
              margin={{ left: 0, right: 0, top: 4, bottom: 4 }}
              testId="resource-utilization-sparkline"
            >
              <Line<ResourceUtilizationPoint>
                dataKey="cpu"
                stroke={CPU_COLOR}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                tooltipConfig={{ displayName: 'CPU' }}
              />
              <Line<ResourceUtilizationPoint>
                dataKey="memory"
                stroke={MEMORY_COLOR}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                tooltipConfig={{ displayName: 'Memory' }}
              />
              <YAxis hide domain={[0, 'auto']} />
              <ChartTooltip<ResourceUtilizationPoint>
                formatValue={(value) =>
                  formatPercentage(Number(value), { roundBy: 0 })
                }
              />
            </ChartContainer>
          </Box>

          <Stack width="100%" flexShrink={0}>
            <InventoryListItem
              testId="resource-utilization-cpu"
              size="sm"
              divider
              slots={{
                startContent: <ColorMarker color={CPU_COLOR} />,
                label: 'CPU',
                endContent: formatPercentage(cpuPct, { roundBy: 0 }),
              }}
              slotProps={{
                label: { color: 'textSecondary' },
                endContent: { variant: 'P12B' },
              }}
            />
            <InventoryListItem
              testId="resource-utilization-memory"
              size="sm"
              slots={{
                startContent: <ColorMarker color={MEMORY_COLOR} />,
                label: 'Memory',
                endContent: formatPercentage(memoryPct, { roundBy: 0 }),
              }}
              slotProps={{
                label: { color: 'textSecondary' },
                endContent: { variant: 'P12B' },
              }}
            />
          </Stack>
        </Stack>
      )}
    </WidgetLayout>
  );
};
